import { Directive, HOST_TAG_NAME, inject, input } from '@angular/core';
import uniqueId from 'lodash-es/uniqueId';
import { UiAccordionDirective } from '../accordion.directive';
import { injectUiAccordionState } from '../accordion.state';
import { UiAccordionItemDirective } from '../item/accordion-item.directive';
import { injectUiAccordionItemState } from '../item/accordion-item.state';

/**
 * Apply the `uiAccordionTrigger` directive to an element that represents the trigger for an accordion item, such as a button
 */
@Directive({
    selector: '[uiAccordionTrigger]',
    exportAs: 'uiAccordionTrigger',
    host: {
        '[id]': 'id()',
        '[attr.type]': 'tagName === "button" ? "button" : null',
        '[attr.data-orientation]': 'accordion().orientation()',
        '[attr.data-open]': 'accordionItem().open() ? "" : null',
        '[attr.data-disabled]': 'accordionItem().disabled() || accordion().disabled() ? "" : null',
        '[attr.aria-controls]': 'accordionItem().contentId()',
        '[attr.aria-expanded]': 'accordionItem().open()',
        '(click)': 'toggle()'
    }
})
export class UiAccordionTriggerDirective<T> {
    /**
     * The id of the trigger
     */
    readonly id = input<string>(uniqueId('ui-accordion-trigger'));

    /**
     * The tag name of the element.
     */
    protected readonly tagName = inject(HOST_TAG_NAME);

    /**
     * Access the parent accordion
     */
    protected readonly accordion = injectUiAccordionState<UiAccordionDirective<T>>();

    /**
     * The item instance
     */
    protected readonly accordionItem = injectUiAccordionItemState<UiAccordionItemDirective<T>>();

    constructor() {
        this.accordionItem().trigger.set(this);
    }

    /**
     * Toggle the accordion item
     */
    toggle(): void {
        if (this.accordionItem().disabled() || this.accordion().disabled()) {
            return;
        }

        const value = this.accordionItem().value();

        if (!value) {
            return;
        }

        this.accordion().toggle(value);
    }
}
